import { useAtomValue } from 'jotai';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { useMemo } from 'react';
import { ocrIsLoadingState } from 'shared/states';

import { getQBooksContext } from '../../../../selectors/qbooks';
import { getColumnDefinitions } from '../QBooksAccountLineItemsSection.config';
import { QBooksAccountLineItemsSectionProps } from '../QBooksAccountLineItemsSection.types';
import { useIsManualMatchingVisible } from './useIsManualMatchingVisible';
import { useRequestExpanded } from './useRequest';
import { useTaxCodes } from './useTaxCodes';

export const useColumnDefinitions = (params: {
    onRemoveMatching: QBooksAccountLineItemsSectionProps['onRemoveMatching'];
}) => {
    const { onRemoveMatching } = params;

    const request = useRequestExpanded();
    const context = useSelector(getQBooksContext);
    const isManualMatchingVisible = useIsManualMatchingVisible();

    const isBill = request.integrationCode === domain.IntegrationCode.QBooksBill;
    const isExpense = request.integrationCode === domain.IntegrationCode.QBooksExpense;

    const isOcrLoading = useAtomValue(ocrIsLoadingState);

    const withTaxCode = request.details.lineAmountType !== domain.LineAmountType.NoTax;
    const withBillable = (isBill || isExpense) && context.hasBillableFeature;
    const withTaxable = (isBill || isExpense) && context.hasTaxableFeature;
    const withCustomer = context.hasCustomerFeature;
    const withClass = context.hasClassFeature;
    const withLinked = isBill && isManualMatchingVisible;

    const taxCodes = useTaxCodes();

    return useMemo(() => {
        return getColumnDefinitions({
            isOcrLoading,
            taxCodes,
            withTaxCode,
            withBillable,
            withTaxable,
            withCustomer,
            withClass,
            withLinked,
            onRemoveMatching,
            currency: request.currency,
        });
    }, [
        isOcrLoading,
        taxCodes,
        withTaxCode,
        withBillable,
        withTaxable,
        withCustomer,
        withClass,
        withLinked,
        onRemoveMatching,
        request.currency,
    ]);
};
